import { supabase } from '../../lib/supabase';

// Phase 6 RPCs (current_institute_for_caller, get_institute_settings,
// consume_*) and the set-institute-mode Edge Function are introduced by the
// 202606071800_institute_mode.sql migration. Until the user re-runs
// `supabase gen types typescript --linked > src/types/database.ts` AFTER
// applying the migration, the typed client's RPC name union does not include
// these. We narrow through `unknown` so this file stays typecheck-clean
// without forcing a codegen step claude can't run itself.
//
// TODO(post-Phase-6 codegen): once src/types/database.ts is regenerated, the
// `as` casts on rpcUnknown become unnecessary — drop them and use
// supabase.rpc('name', ...) directly.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{
    data: unknown;
    // `code` is PostgREST's machine-readable error code (e.g. PGRST202 for a
    // function that isn't in the schema cache yet). Optional because the shape
    // is hand-written here rather than generated.
    error: { message: string; code?: string } | null;
  }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type SessionFilter = 'upcoming' | 'past' | 'all';

export interface InstituteSettings {
  institute_id: string;
  name: string;
  slug: string;
  ai_tutor_enabled: boolean;
  pdf_ingest_enabled: boolean;
  /** Default for new sittings. A session may override it; practice always uses AI. */
  ai_review_enabled: boolean;
  /** Path in the institute-branding bucket. Null = no logo set. */
  logo_path: string | null;
  caller_can_edit: boolean;
}

export interface CurrentInstitute {
  institute_id: string;
  role: 'owner' | 'admin' | 'teacher' | 'examiner';
}

export const INSTITUTE_QUERY_KEYS = {
  current: ['institute', 'current'] as const,
  settings: (instituteId: string) => ['institute', 'settings', instituteId] as const,
  stats: {
    studentsCount: (instituteId: string) =>
      ['institute', 'stats', 'students_count', instituteId] as const,
    upcomingSessions: (instituteId: string) =>
      ['institute', 'stats', 'upcoming_sessions', instituteId] as const,
    pendingGrading: (instituteId: string) =>
      ['institute', 'stats', 'pending_grading', instituteId] as const,
    monthlyMocks: (instituteId: string) =>
      ['institute', 'stats', 'monthly_mocks', instituteId] as const,
  },
  students: (instituteId: string) => ['institute', 'students', instituteId] as const,
  // Roster view that also pulls archived rows. Kept distinct from `students`
  // so the active-only pickers (batch picker, new-session picker) never read
  // archived students out of a shared cache entry.
  studentsWithArchived: (instituteId: string) =>
    ['institute', 'students', instituteId, 'with_archived'] as const,
  batches: (instituteId: string) => ['institute', 'batches', instituteId] as const,
  // Every (batch, student) pair in the institute, for the roster's batch
  // filter. Nested under `batches` on purpose: archiving a student, saving a
  // batch's roster and deleting a batch all invalidate that prefix already.
  batchMemberships: (instituteId: string) =>
    ['institute', 'batches', instituteId, 'memberships'] as const,
  batchStudents: (batchId: string) => ['institute', 'batch_students', batchId] as const,
  // Per-batch attendance counts for ONE date, powering the dashboard batch
  // switcher. Date is part of the key: the board's date bar steps backwards and
  // the chips must describe the date on screen, not today.
  attendanceSummary: (instituteId: string, date: string) =>
    ['institute', 'attendance_summary', instituteId, date] as const,
  sessions: (instituteId: string, filter: SessionFilter) =>
    ['institute', 'sessions', instituteId, filter] as const,
  sessionDetail: (sessionId: string) => ['institute', 'session_detail', sessionId] as const,
  sessionInvitees: (sessionId: string) => ['institute', 'session_invitees', sessionId] as const,
  availableTests: (instituteId: string) => ['institute', 'available_tests', instituteId] as const,
};

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('Empty response from Supabase');
  return data;
}

export async function getCurrentInstitute(): Promise<CurrentInstitute | null> {
  const { data, error } = await rpcUnknown.rpc('current_institute_for_caller');
  if (error !== null) throw new Error(error.message);
  const rows = data as CurrentInstitute[] | null;
  if (rows === null || rows.length === 0) return null;
  return rows[0] ?? null;
}

export async function getInstituteSettings(instituteId: string): Promise<InstituteSettings> {
  const { data, error } = await rpcUnknown.rpc('get_institute_settings', {
    p_institute_id: instituteId,
  });
  const rows = unwrap(data as InstituteSettings[] | null, error);
  if (rows.length === 0) throw new Error('Institute not found');
  return rows[0] as InstituteSettings;
}

export interface SetInstituteFeaturesArgs {
  institute_id: string;
  ai_tutor_enabled: boolean;
  pdf_ingest_enabled: boolean;
  ai_review_enabled: boolean;
}

export interface SetInstituteFeaturesResult {
  institute_id: string;
  ai_tutor_enabled: boolean;
  pdf_ingest_enabled: boolean;
  ai_review_enabled: boolean;
}

export async function setInstituteFeatures(
  args: SetInstituteFeaturesArgs,
): Promise<SetInstituteFeaturesResult> {
  const { data, error } = await rpcUnknown.rpc('set_institute_features', {
    p_institute_id: args.institute_id,
    p_ai_tutor_enabled: args.ai_tutor_enabled,
    p_pdf_ingest_enabled: args.pdf_ingest_enabled,
    p_ai_review_enabled: args.ai_review_enabled,
  });
  if (error !== null) throw new Error(error.message);
  const rows = data as SetInstituteFeaturesResult[] | null;
  if (rows === null || rows.length === 0) {
    throw new Error('set_institute_features returned no row');
  }
  return rows[0] as SetInstituteFeaturesResult;
}

export interface UpdateInstituteLogoResult {
  institute_id: string;
  logo_path: string | null;
}

/** Same admin-only gate as `set_institute_features` — a logo is institute identity, not a per-member setting. */
export async function updateInstituteLogo(
  instituteId: string,
  logoPath: string | null,
): Promise<UpdateInstituteLogoResult> {
  const { data, error } = await rpcUnknown.rpc('institute_update_logo', {
    p_institute_id: instituteId,
    p_logo_path: logoPath,
  });
  if (error !== null) throw new Error(error.message);
  const rows = data as UpdateInstituteLogoResult[] | null;
  if (rows === null || rows.length === 0) {
    throw new Error('institute_update_logo returned no row');
  }
  return rows[0] as UpdateInstituteLogoResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 10 — Dashboard stats + students list + invites
// ─────────────────────────────────────────────────────────────────────────────

// Each dashboard card runs its own count query so a slow one doesn't block
// the rest of the dashboard. Tables RLS-scope to the caller's institute
// already, but every query still filters by institute_id explicitly so a
// member of multiple institutes only sees the requested one.

function unwrapCount(count: number | null, error: { message: string } | null): number {
  if (error !== null) throw new Error(error.message);
  return count ?? 0;
}

export async function getStudentsCount(instituteId: string): Promise<number> {
  // Active roster only — archived students are off the books for the KPI.
  const { count, error } = await supabase
    .from('students')
    .select('id', { count: 'exact', head: true })
    .eq('institute_id', instituteId)
    .is('archived_at', null);
  return unwrapCount(count, error);
}

export async function getUpcomingSessionsCount(instituteId: string): Promise<number> {
  const { count, error } = await supabase
    .from('exam_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('institute_id', instituteId)
    .eq('status', 'scheduled')
    .gt('scheduled_start', new Date().toISOString());
  return unwrapCount(count, error);
}

export async function getPendingGradingCount(instituteId: string): Promise<number> {
  // exam_attempts has no institute_id column; filter via the linked session
  // using an !inner join so PostgREST applies the where-clause on the joined
  // table.
  const { count, error } = await supabase
    .from('exam_attempts')
    .select('id, exam_sessions!inner(institute_id)', { count: 'exact', head: true })
    .eq('status', 'grading')
    .eq('exam_sessions.institute_id', instituteId);
  return unwrapCount(count, error);
}

export async function getMonthlyMocksCount(instituteId: string): Promise<number> {
  const startOfMonth = new Date();
  startOfMonth.setUTCDate(1);
  startOfMonth.setUTCHours(0, 0, 0, 0);
  const { count, error } = await supabase
    .from('mock_billing_events')
    .select('id', { count: 'exact', head: true })
    .eq('institute_id', instituteId)
    .gte('created_at', startOfMonth.toISOString());
  return unwrapCount(count, error);
}

export type StudentInviteStatus = 'invited' | 'joined';
export type StudentLoginKind = 'email' | 'credential';

export interface InstituteStudentRow {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  phone: string;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
  status: StudentInviteStatus;
  last_sign_in_at: string | null;
  created_at: string;
  // null = active; an ISO timestamp = archived (soft-deleted) at that time.
  archived_at: string | null;
  // 'credential' = signs in with a username + password at /i/<slug>/login;
  // 'email' = the classic magic-link invite. login_handle is the username for
  // credential students, null for email ones.
  login_handle: string | null;
  login_kind: StudentLoginKind;
}

export async function listInstituteStudents(
  instituteId: string,
  includeArchived = false,
): Promise<InstituteStudentRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_students', {
    p_institute_id: instituteId,
    p_include_archived: includeArchived,
  });
  if (error !== null) throw new Error(error.message);
  return (data as InstituteStudentRow[] | null) ?? [];
}

export interface SetStudentArchivedResult {
  student_id: string;
  archived_at: string | null;
}

/**
 * Archive (soft-delete) or restore a student. Archiving also pulls them from
 * every batch and any upcoming exam sessions; their past attempts, results and
 * attendance are kept. Allowed for any active member of the student's institute.
 */
export async function setStudentArchived(
  studentId: string,
  archived: boolean,
): Promise<SetStudentArchivedResult> {
  const { data, error } = await rpcUnknown.rpc('set_student_archived', {
    p_student_id: studentId,
    p_archived: archived,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as SetStudentArchivedResult[] | null) ?? [];
  return (
    rows[0] ?? { student_id: studentId, archived_at: archived ? new Date().toISOString() : null }
  );
}

export interface InviteStudentArgs {
  institute_id: string;
  email: string;
  name: string;
  phone: string;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
}

export interface InviteStudentResult {
  email: string;
  ok: boolean;
  user_id: string | null;
  created_user: boolean;
  invite_sent: boolean;
  student_id: string | null;
  error?: string;
  error_code?: string;
}

export async function inviteStudent(args: InviteStudentArgs): Promise<InviteStudentResult> {
  const { data, error } = await supabase.functions.invoke<InviteStudentResult>('invite-student', {
    body: args,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('invite-student returned no body');
  return data;
}

export interface InviteStudentRowInput {
  email: string;
  name: string;
  phone: string;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
}

export interface InviteStudentBulkResponse {
  processed: number;
  succeeded: number;
  results: InviteStudentResult[];
}

export async function inviteStudentsBulk(
  instituteId: string,
  students: InviteStudentRowInput[],
): Promise<InviteStudentBulkResponse> {
  const { data, error } = await supabase.functions.invoke<InviteStudentBulkResponse>(
    'invite-student-bulk',
    { body: { institute_id: instituteId, students } },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('invite-student-bulk returned no body');
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 90 — Credential provisioning (no-email student login)
// ─────────────────────────────────────────────────────────────────────────────

export interface ProvisionStudentArgs {
  institute_id: string;
  username: string;
  name: string;
  phone: string | null;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
}

export interface ProvisionStudentResult {
  ok: boolean;
  username: string;
  user_id: string | null;
  student_id: string | null;
  // Shown ONCE — never persisted by us. Null on failure.
  password: string | null;
  // Origin-relative, e.g. "/i/acme/login". Prepend window.location.origin.
  login_path: string;
  error?: string;
  error_code?: string;
}

export async function provisionStudent(
  args: ProvisionStudentArgs,
): Promise<ProvisionStudentResult> {
  const { data, error } = await supabase.functions.invoke<ProvisionStudentResult>(
    'provision-student',
    { body: args },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('provision-student returned no body');
  return data;
}

export interface ProvisionStudentRowInput {
  username: string;
  name: string;
  phone: string | null;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
}

export interface ProvisionStudentBulkResponse {
  processed: number;
  succeeded: number;
  results: ProvisionStudentResult[];
}

export async function provisionStudentsBulk(
  instituteId: string,
  students: ProvisionStudentRowInput[],
): Promise<ProvisionStudentBulkResponse> {
  const { data, error } = await supabase.functions.invoke<ProvisionStudentBulkResponse>(
    'provision-student-bulk',
    { body: { institute_id: instituteId, students } },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('provision-student-bulk returned no body');
  return data;
}

export interface ResetStudentPasswordResult {
  ok: boolean;
  username: string;
  password: string;
  login_path: string;
}

export async function resetStudentPassword(studentId: string): Promise<ResetStudentPasswordResult> {
  const { data, error } = await supabase.functions.invoke<ResetStudentPasswordResult>(
    'reset-student-password',
    { body: { student_id: studentId } },
  );
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('reset-student-password returned no body');
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 11 — Batches CRUD + atomic student assignment
// ─────────────────────────────────────────────────────────────────────────────

export interface BatchRow {
  id: string;
  name: string;
  schedule_label: string | null;
  student_count: number;
  created_at: string;
}

export interface BatchStudentRow {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  phone: string;
  roll_no: string | null;
  exam_type: 'academic' | 'general';
  target_band: number | null;
  assigned_at: string;
}

export async function listInstituteBatches(instituteId: string): Promise<BatchRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_batches', {
    p_institute_id: instituteId,
  });
  if (error !== null) throw new Error(error.message);
  // institute_view_batches returns student_count as bigint → PostgREST sends a
  // numeric string. Normalize to number here so the UI never needs to parse.
  const rows =
    (data as Array<Omit<BatchRow, 'student_count'> & { student_count: number | string }>) ?? [];
  return rows.map((r) => ({
    ...r,
    student_count:
      typeof r.student_count === 'string' ? parseInt(r.student_count, 10) : r.student_count,
  }));
}

export async function getBatchStudents(batchId: string): Promise<BatchStudentRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_batch_students', {
    p_batch_id: batchId,
  });
  if (error !== null) throw new Error(error.message);
  return (data as BatchStudentRow[] | null) ?? [];
}

export interface BatchMembership {
  batch_id: string;
  student_id: string;
}

// PostgREST's default max-rows on a hosted project. A response is truncated
// to it WITHOUT an error, so a single request would quietly lose memberships
// past row 1000 — and a lost membership reads as "not in any batch", the very
// thing the roster filter exists to find.
const MEMBERSHIP_PAGE = 1000;

/**
 * Every (batch, student) pair in the institute. A direct table read — the
 * `batch_students_tenant_select` policy already scopes it to the caller's
 * institutes — paged under a stable order so no page can skip or repeat rows.
 */
export async function listBatchMemberships(instituteId: string): Promise<BatchMembership[]> {
  const out: BatchMembership[] = [];
  let from = 0;
  let more = true;
  while (more) {
    const { data, error } = await supabase
      .from('batch_students')
      .select('batch_id, student_id, batches!inner(institute_id)')
      .eq('batches.institute_id', instituteId)
      .order('batch_id')
      .order('student_id')
      .range(from, from + MEMBERSHIP_PAGE - 1);
    if (error !== null) throw new Error(error.message);
    for (const row of data) out.push({ batch_id: row.batch_id, student_id: row.student_id });
    more = data.length === MEMBERSHIP_PAGE;
    from += MEMBERSHIP_PAGE;
  }
  return out;
}

export interface SetBatchStudentsResult {
  added: number;
  removed: number;
  kept: number;
}

export async function setBatchStudents(
  batchId: string,
  studentIds: string[],
): Promise<SetBatchStudentsResult> {
  const { data, error } = await rpcUnknown.rpc('set_batch_students', {
    p_batch_id: batchId,
    p_student_ids: studentIds,
  });
  if (error !== null) throw new Error(error.message);
  const rows = data as SetBatchStudentsResult[] | null;
  if (rows === null || rows.length === 0) {
    return { added: 0, removed: 0, kept: 0 };
  }
  return rows[0] as SetBatchStudentsResult;
}

export interface CreateBatchInput {
  institute_id: string;
  name: string;
  schedule_label: string | null;
}

export async function createBatch(input: CreateBatchInput): Promise<BatchRow> {
  const { data, error } = await supabase
    .from('batches')
    .insert({
      institute_id: input.institute_id,
      name: input.name,
      schedule_label: input.schedule_label,
    })
    .select('id, name, schedule_label, created_at')
    .single();
  if (error !== null) {
    // Postgres unique_violation code is 23505 — surface a friendlier message.
    if (error.code === '23505') {
      throw new Error('A batch with that name already exists');
    }
    throw new Error(error.message);
  }
  return { ...data, student_count: 0 };
}

export interface UpdateBatchInput {
  id: string;
  name: string;
  schedule_label: string | null;
}

export async function updateBatch(input: UpdateBatchInput): Promise<void> {
  const { error } = await supabase
    .from('batches')
    .update({ name: input.name, schedule_label: input.schedule_label })
    .eq('id', input.id);
  if (error !== null) {
    if (error.code === '23505') {
      throw new Error('A batch with that name already exists');
    }
    throw new Error(error.message);
  }
}

export async function deleteBatch(batchId: string): Promise<void> {
  const { error } = await supabase.from('batches').delete().eq('id', batchId);
  if (error !== null) throw new Error(error.message);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 12 — Schedule mock exam + sessions list/detail
// ─────────────────────────────────────────────────────────────────────────────

export type ExamType = 'academic' | 'general';
export type SessionStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

export interface AvailableTest {
  id: string;
  title: string;
  exam_type: ExamType;
  institute_id: string | null;
  total_duration_minutes: number;
  /** Phase 161: a question-type drill rather than a mock. Assignable — that is
   *  the point — but it must be LABELLED, because a teacher cannot otherwise
   *  tell an 8-question drill from a 40-question paper in a flat dropdown. */
  is_practice_set: boolean;
}

// Tests visible to the caller: published global bank + own-institute tests.
// RLS gates row visibility, so a plain select is safe.
export async function listAvailableTests(instituteId: string): Promise<AvailableTest[]> {
  const { data, error } = await supabase
    .from('tests')
    .select('id, title, exam_type, institute_id, total_duration_minutes, status')
    .eq('status', 'published')
    .or(`institute_id.is.null,institute_id.eq.${instituteId}`)
    .order('title');
  if (error !== null) throw new Error(error.message);

  // A separate read rather than an embedded join: practice_sets does not exist
  // until the phase-161 migration is applied, and an embedded select would fail
  // the WHOLE query. On error every row is simply an ordinary test, which is
  // exactly what it was before the feature existed.
  const drillIds = new Set<string>();
  const { data: drills } = await supabase.from('practice_sets').select('test_id');
  for (const row of drills ?? []) {
    if (typeof row.test_id === 'string') drillIds.add(row.test_id);
  }

  return (data ?? []).map((r) => ({
    id: r.id,
    title: r.title,
    exam_type: r.exam_type as ExamType,
    institute_id: r.institute_id,
    total_duration_minutes: r.total_duration_minutes,
    is_practice_set: drillIds.has(r.id),
  }));
}

export interface SessionRow {
  id: string;
  test_id: string;
  test_title: string;
  scheduled_start: string;
  scheduled_end: string;
  location: string | null;
  status: SessionStatus;
  invitee_count: number;
  created_at: string;
}

export async function listInstituteSessions(
  instituteId: string,
  filter: SessionFilter,
): Promise<SessionRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_sessions', {
    p_institute_id: instituteId,
    p_filter: filter,
  });
  if (error !== null) throw new Error(error.message);
  const rows =
    (data as Array<Omit<SessionRow, 'invitee_count'> & { invitee_count: number | string }>) ?? [];
  return rows.map((r) => ({
    ...r,
    invitee_count:
      typeof r.invitee_count === 'string' ? parseInt(r.invitee_count, 10) : r.invitee_count,
  }));
}

export interface SessionDetail {
  id: string;
  institute_id: string;
  test_id: string;
  test_title: string;
  exam_type: ExamType;
  scheduled_start: string;
  scheduled_end: string;
  location: string | null;
  status: SessionStatus;
  cancellation_reason: string | null;
  invitee_count: number;
  created_at: string;
  /** The raw override: null means "inherit the institute default". */
  ai_review_enabled: boolean | null;
  /** What this sitting will actually do, override already applied. */
  ai_review_resolved: boolean;
}

export async function getSessionDetail(sessionId: string): Promise<SessionDetail | null> {
  const { data, error } = await rpcUnknown.rpc('institute_view_session_detail', {
    p_session_id: sessionId,
  });
  if (error !== null) throw new Error(error.message);
  const rows =
    (data as Array<Omit<SessionDetail, 'invitee_count'> & { invitee_count: number | string }>) ??
    [];
  if (rows.length === 0) return null;
  const r = rows[0] as Omit<SessionDetail, 'invitee_count'> & { invitee_count: number | string };
  return {
    ...r,
    invitee_count:
      typeof r.invitee_count === 'string' ? parseInt(r.invitee_count, 10) : r.invitee_count,
  };
}

export type AttendanceStatus = 'invited' | 'present' | 'absent' | 'late' | 'excused';

export interface SessionInvitee {
  invitee_id: string;
  student_id: string;
  email: string;
  display_name: string;
  roll_no: string | null;
  exam_type: ExamType;
  attendance_status: AttendanceStatus;
  invited_at: string;
}

export async function getSessionInvitees(sessionId: string): Promise<SessionInvitee[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_session_invitees', {
    p_session_id: sessionId,
  });
  if (error !== null) throw new Error(error.message);
  return (data as SessionInvitee[] | null) ?? [];
}

export interface CreateExamSessionInput {
  institute_id: string;
  test_id: string;
  scheduled_start: string;
  scheduled_end: string;
  location: string | null;
  student_ids: string[];
  /** null = inherit the institute's AI review default. */
  ai_review_enabled: boolean | null;
}

export async function createExamSession(input: CreateExamSessionInput): Promise<string> {
  const { data, error } = await rpcUnknown.rpc('create_exam_session', {
    p_institute_id: input.institute_id,
    p_test_id: input.test_id,
    p_scheduled_start: input.scheduled_start,
    p_scheduled_end: input.scheduled_end,
    p_location: input.location,
    p_student_ids: input.student_ids,
    p_ai_review_enabled: input.ai_review_enabled,
  });
  if (error !== null) throw new Error(error.message);
  return data as string;
}

export interface SessionAiReview {
  session_id: string;
  ai_review_enabled: boolean | null;
  ai_review_resolved: boolean;
}

/**
 * The one field a scheduled sitting can still be edited on after creation.
 * Pass null to fall back to the institute default.
 */
export async function setSessionAiReview(
  sessionId: string,
  enabled: boolean | null,
): Promise<SessionAiReview> {
  const { data, error } = await rpcUnknown.rpc('set_exam_session_ai_review', {
    p_session_id: sessionId,
    p_enabled: enabled,
  });
  if (error !== null) throw new Error(error.message);
  const rows = data as SessionAiReview[] | null;
  if (rows === null || rows.length === 0) {
    throw new Error('set_exam_session_ai_review returned no row');
  }
  return rows[0] as SessionAiReview;
}

export async function cancelExamSession(sessionId: string, reason: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('cancel_exam_session', {
    p_session_id: sessionId,
    p_reason: reason,
  });
  if (error !== null) throw new Error(error.message);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 13 — Attendance
// ─────────────────────────────────────────────────────────────────────────────

export type ActiveAttendanceStatus = Exclude<AttendanceStatus, 'invited'>;

export interface AttendanceAssignment {
  student_id: string;
  status: ActiveAttendanceStatus;
}

export interface MarkAttendanceResult {
  updated_count: number;
  audit_count: number;
}

export async function markAttendance(
  sessionId: string,
  assignments: AttendanceAssignment[],
): Promise<MarkAttendanceResult> {
  const { data, error } = await rpcUnknown.rpc('mark_attendance', {
    p_session_id: sessionId,
    p_assignments: assignments,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as MarkAttendanceResult[] | null) ?? [];
  return rows[0] ?? { updated_count: 0, audit_count: 0 };
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 13.5 — Daily attendance for batches
// ─────────────────────────────────────────────────────────────────────────────

// Workday mask bit layout: bit 0 = Sun, bit 1 = Mon, …, bit 6 = Sat.
// Mon–Fri = 62, every day = 127.
export const WORKDAY_MASK_MON_FRI = 62;
export const WORKDAY_MASK_EVERY_DAY = 127;
export const WORKDAY_LABELS: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function maskToWorkdays(mask: number): boolean[] {
  return WORKDAY_LABELS.map((_, i) => (mask & (1 << i)) !== 0);
}
export function workdaysToMask(workdays: boolean[]): number {
  let mask = 0;
  workdays.forEach((on, i) => {
    if (on) mask |= 1 << i;
  });
  return mask;
}

export interface BatchWorkdayConfig {
  workday_mask: number;
  updated_at: string | null;
}

export async function getBatchWorkdayConfig(batchId: string): Promise<BatchWorkdayConfig> {
  const { data, error } = await rpcUnknown.rpc('get_batch_workday_config', {
    p_batch_id: batchId,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as BatchWorkdayConfig[] | null) ?? [];
  return rows[0] ?? { workday_mask: WORKDAY_MASK_MON_FRI, updated_at: null };
}

export async function upsertBatchWorkdayConfig(batchId: string, mask: number): Promise<number> {
  const { data, error } = await rpcUnknown.rpc('upsert_batch_workday_config', {
    p_batch_id: batchId,
    p_workday_mask: mask,
  });
  if (error !== null) throw new Error(error.message);
  return data as number;
}

export interface BatchHoliday {
  holiday_date: string; // ISO date
  reason: string | null;
  created_at: string;
}

export async function listBatchHolidays(
  batchId: string,
  fromDate: string,
  toDate: string,
): Promise<BatchHoliday[]> {
  const { data, error } = await rpcUnknown.rpc('list_batch_holidays', {
    p_batch_id: batchId,
    p_from: fromDate,
    p_to: toDate,
  });
  if (error !== null) throw new Error(error.message);
  return (data as BatchHoliday[] | null) ?? [];
}

export async function setBatchHoliday(
  batchId: string,
  date: string,
  reason: string | null,
): Promise<string> {
  const { data, error } = await rpcUnknown.rpc('set_batch_holiday', {
    p_batch_id: batchId,
    p_date: date,
    p_reason: reason,
  });
  if (error !== null) throw new Error(error.message);
  return data as string;
}

export async function removeBatchHoliday(batchId: string, date: string): Promise<boolean> {
  const { data, error } = await rpcUnknown.rpc('remove_batch_holiday', {
    p_batch_id: batchId,
    p_date: date,
  });
  if (error !== null) throw new Error(error.message);
  return data === true;
}

export type DailyAttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

export interface DailyAttendanceRow {
  is_workday: boolean;
  holiday_reason: string | null;
  student_id: string;
  email: string;
  display_name: string;
  roll_no: string | null;
  status: DailyAttendanceStatus | null;
  notes: string | null;
  marked_at: string | null;
  /**
   * true when `status` was written by the nightly auto-absent job rather than
   * a person (see 202608210900_phase135_attendance_auto_absent.sql). Optional
   * so payloads from before that migration — and existing test fixtures —
   * still type-check; treat a missing value as `false`.
   */
  auto_marked?: boolean;
}

export interface DailyAttendanceDay {
  is_workday: boolean;
  holiday_reason: string | null;
  rows: DailyAttendanceRow[];
}

export async function getBatchDailyAttendance(
  batchId: string,
  date: string,
): Promise<DailyAttendanceDay> {
  const { data, error } = await rpcUnknown.rpc('get_batch_daily_attendance', {
    p_batch_id: batchId,
    p_date: date,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as DailyAttendanceRow[] | null) ?? [];
  if (rows.length === 0) {
    return { is_workday: true, holiday_reason: null, rows: [] };
  }
  const head = rows[0] as DailyAttendanceRow;
  return { is_workday: head.is_workday, holiday_reason: head.holiday_reason, rows };
}

export interface DailyAttendanceAssignment {
  student_id: string;
  status: DailyAttendanceStatus;
  notes?: string | null;
}

export interface MarkDailyAttendanceResult {
  updated_count: number;
  audit_count: number;
}

export async function markDailyAttendance(
  batchId: string,
  date: string,
  assignments: DailyAttendanceAssignment[],
): Promise<MarkDailyAttendanceResult> {
  const { data, error } = await rpcUnknown.rpc('mark_daily_attendance', {
    p_batch_id: batchId,
    p_date: date,
    p_assignments: assignments,
  });
  if (error !== null) throw new Error(error.message);
  const rows = (data as MarkDailyAttendanceResult[] | null) ?? [];
  return rows[0] ?? { updated_count: 0, audit_count: 0 };
}

export interface ClearDailyAttendanceResult {
  cleared_count: number;
  audit_count: number;
}

/**
 * Erase marks so the cells go back to "unmarked".
 *
 * `mark_daily_attendance` can only ever write one of the four real statuses, so
 * this is the only way a cell returns to blank. The RPC ships in
 * 202608111000_phase130_attendance_clear.sql — until that migration is applied
 * PostgREST answers PGRST202, which we translate into a sentence a teacher can
 * act on instead of "Could not find the function … in the schema cache".
 */
export async function clearDailyAttendance(
  batchId: string,
  date: string,
  studentIds: readonly string[],
): Promise<ClearDailyAttendanceResult> {
  const { data, error } = await rpcUnknown.rpc('clear_daily_attendance', {
    p_batch_id: batchId,
    p_date: date,
    p_student_ids: studentIds,
  });
  if (error !== null) {
    if (isMissingFunctionError(error)) {
      throw new Error(
        'Clearing a mark needs migration 202608111000_phase130_attendance_clear.sql to be applied first.',
      );
    }
    throw new Error(error.message);
  }
  const rows = (data as ClearDailyAttendanceResult[] | null) ?? [];
  return rows[0] ?? { cleared_count: 0, audit_count: 0 };
}

export const BATCH_ATTENDANCE_QUERY_KEYS = {
  workdayConfig: (batchId: string) => ['institute', 'batch_workday_config', batchId] as const,
  holidays: (batchId: string, from: string, to: string) =>
    ['institute', 'batch_holidays', batchId, from, to] as const,
  day: (batchId: string, date: string) =>
    ['institute', 'batch_daily_attendance', batchId, date] as const,
  month: (batchId: string, year: number, month: number) =>
    ['institute', 'batch_monthly_view', batchId, year, month] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-batch attendance summary for one date (dashboard batch switcher)
// ─────────────────────────────────────────────────────────────────────────────

export interface AttendanceSummaryRow {
  batch_id: string;
  name: string;
  schedule_label: string | null;
  student_count: number;
  is_workday: boolean;
  holiday_reason: string | null;
  marked_count: number;
  present_count: number;
  absent_count: number;
  late_count: number;
  excused_count: number;
}

/**
 * PostgREST reports an un-deployed function as PGRST202 ("Could not find the
 * function … in the schema cache"). We treat that one case as "no summary
 * available" rather than an error, because the attendance board must keep
 * working on the pre-existing RPCs until this migration is applied.
 */
function isMissingFunctionError(error: { message: string; code?: string }): boolean {
  if (error.code === 'PGRST202') return true;
  return /schema cache|does not exist/i.test(error.message);
}

/**
 * Counts per batch for a single date. Returns `null` — not an error — when the
 * migration behind it has not been applied yet; callers then render the rail
 * without badges. Every other failure still throws.
 */
export async function getInstituteAttendanceSummary(
  instituteId: string,
  date: string,
): Promise<AttendanceSummaryRow[] | null> {
  const { data, error } = await rpcUnknown.rpc('institute_attendance_summary', {
    p_institute_id: instituteId,
    p_date: date,
  });
  if (error !== null) {
    if (isMissingFunctionError(error)) return null;
    throw new Error(error.message);
  }
  // Counts come back from Postgres as int, but student_count is derived from a
  // count(*) elsewhere in this file that arrives as a numeric string — normalize
  // defensively so the UI never has to parse.
  const rows = (data as Array<Record<string, unknown>> | null) ?? [];
  const num = (v: unknown): number => (typeof v === 'string' ? parseInt(v, 10) : (v as number));
  return rows.map((r) => ({
    batch_id: r.batch_id as string,
    name: r.name as string,
    schedule_label: (r.schedule_label as string | null) ?? null,
    student_count: num(r.student_count),
    is_workday: r.is_workday === true,
    holiday_reason: (r.holiday_reason as string | null) ?? null,
    marked_count: num(r.marked_count),
    present_count: num(r.present_count),
    absent_count: num(r.absent_count),
    late_count: num(r.late_count),
    excused_count: num(r.excused_count),
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Monthly grid view — single JSONB round trip
// ─────────────────────────────────────────────────────────────────────────────

export interface MonthlyAttendanceDay {
  date: string; // YYYY-MM-DD
  is_workday: boolean;
  holiday_reason: string | null;
}

export interface MonthlyAttendanceStudent {
  student_id: string;
  display_name: string;
  email: string;
  roll_no: string | null;
}

export interface MonthlyAttendanceMark {
  student_id: string;
  date: string;
  status: DailyAttendanceStatus;
  notes: string | null;
  /**
   * true when this mark was written by the nightly auto-absent job rather
   * than a person (see 202608210900_phase135_attendance_auto_absent.sql).
   * Optional so payloads from before that migration — and existing test
   * fixtures — still type-check; treat a missing value as `false`.
   */
  auto_marked?: boolean;
}

export interface MonthlyAttendanceView {
  days: MonthlyAttendanceDay[];
  students: MonthlyAttendanceStudent[];
  marks: MonthlyAttendanceMark[];
}

export async function getBatchMonthlyView(
  batchId: string,
  year: number,
  month: number,
): Promise<MonthlyAttendanceView> {
  const { data, error } = await rpcUnknown.rpc('get_batch_monthly_view', {
    p_batch_id: batchId,
    p_year: year,
    p_month: month,
  });
  if (error !== null) throw new Error(error.message);
  return (
    (data as MonthlyAttendanceView | null) ?? {
      days: [],
      students: [],
      marks: [],
    }
  );
}
